import { useEffect, useState } from 'react';
import { Form, useActionData, useLoaderData, useNavigate, useNavigation, useSubmit } from '@remix-run/react';
import { ActionFunctionArgs, LoaderFunctionArgs, json, redirect } from '@remix-run/node';
import { useAuth } from '~/lib/auth-context';
import { ToastProvider } from '~/contexts/toast-context';
import { ToastContainer } from '~/design-system/components/Toast/ToastContainer';
import { SignInUI, SignInWaiting } from '~/design-system/components/Auth/SignInUI/SignInUI';
import { TurnstileWidget } from '~/design-system/components/Auth/SignInUI/TurnstileWidget';
import { track } from '~/lib/posthog';
import { createSupabaseServerClient } from '~/lib/supabase-utils';
import { postSignInDestination } from '~/lib/return-to.server';
import { signUpEmailRefusal, turnstileSiteKey } from '~/lib/sign-up-email.server';
import { DESKTOP_AUTH_CALLBACK, isDesktopAppRequest, providerSignInUrl } from '~/lib/desktop-sign-in.server';
import { NOT_OPEN_YET, openToNewAccounts } from '~/lib/opening.server';
import type { MetaFunction } from '@remix-run/node';
import { brandFontLinks } from '~/deck2/fonts';

export const meta: MetaFunction = () => [{ title: 'Sign in' }];

// The landing's type, which the sign-in pages are set in.
export const links = brandFontLinks;

export const getSiteUrl = (request?: Request) => {
  if (typeof window !== 'undefined') {
    return window.location.origin;
  }
  // Server-side: prefer the actual origin the request came from (the browser's Origin header) so the
  // magic-link / OAuth callback returns to wherever the user really is — local dev on ANY port,
  // staging, or prod — instead of one hardcoded APP_URL. Falls back to APP_URL when Origin is absent.
  const requestOrigin = request?.headers.get('origin');
  if (requestOrigin) return requestOrigin;
  return process.env.APP_URL;
};

type ActionData =
  | { error: string; success?: undefined; message?: undefined; details?: string; cooldown?: boolean }
  | { error?: undefined; success: true; message: string }
  | { error?: undefined; success?: undefined; openInBrowser: string };

const isError = (
  data: ActionData | undefined,
): data is { error: string; details?: string; cooldown?: boolean } =>
  data !== undefined && 'error' in data;

const isSuccess = (data: ActionData | undefined): data is { success: true; message: string } =>
  data !== undefined && 'success' in data;

// Someone who is already signed in has no business on the sign-in form, and bouncing them from an
// effect meant rendering something first — which was the magic-link verifying screen. Sent on the
// server instead, same as `/`. The magic-link and OAuth returns land on /auth/callback, never here,
// so this cannot intercept a sign-in that is still in flight.
export const loader = async ({ request }: LoaderFunctionArgs) => {
  const response = new Response();
  const supabase = createSupabaseServerClient(request, response);
  const { data: { session } } = await supabase.auth.getSession();

  if (session) {
    // Onboarding first; then the page that asked to be returned to (`/connect`), else the dashboard.
    const redirectTo = await postSignInDestination(request, response.headers);
    // It falls back to /sign-in when the cookie has a session but no usable token. Honouring that
    // from this route would bounce against itself forever, so show the form and let them sign in.
    if (redirectTo !== '/sign-in') {
      return redirect(redirectTo, { headers: response.headers });
    }
  }

  return json({ turnstileSiteKey: turnstileSiteKey() }, { headers: response.headers });
};

export const action = async ({ request }: ActionFunctionArgs) => {
  const response = new Response();
  const serverScopedSupabase = createSupabaseServerClient(request, response);

  const formData = await request.formData();

  // Google OAuth: hand off to the provider; the allowlist is enforced in /auth/callback.
  if (formData.get('intent')?.toString() === 'google') {
    const fromApp = isDesktopAppRequest(request);
    const { data, error } = await serverScopedSupabase.auth.signInWithOAuth({
      provider: 'google',
      options: fromApp
        ? // The app's own link brings the code back to the app. select_account shows the accounts the
          // browser is already signed in to, instead of an empty email field.
          { redirectTo: DESKTOP_AUTH_CALLBACK, skipBrowserRedirect: true, queryParams: { prompt: 'select_account' } }
        : { redirectTo: `${getSiteUrl(request)}/auth/callback` },
    });
    if (error || !data?.url) {
      return json<ActionData>(
        { error: 'Could not start Google sign-in. Please try again.' },
        { headers: response.headers },
      );
    }
    if (fromApp) {
      // In Deck for Mac, Google opens in the default browser, where the person is already signed in.
      // The verifier cookie in these headers stays in the app, which is where the code comes back.
      const googleUrl = await providerSignInUrl(data.url);
      if (googleUrl) return json<ActionData>({ openInBrowser: googleUrl }, { headers: response.headers });
    }
    return redirect(data.url, { headers: response.headers });
  }

  const email = formData.get('email')?.toString().toLowerCase();

  if (!email) {
    return json<ActionData>({ error: 'Email is required' }, { headers: response.headers });
  }

  const refusal = await signUpEmailRefusal(email);
  if (refusal) {
    return json<ActionData>({ error: refusal }, { headers: response.headers });
  }
  const captchaToken = formData.get('captchaToken')?.toString() || undefined;

  try {
    // Desktop app (Electron): send the magic link back to the app via the usedeck:// deep link
    // instead of the browser. This action runs server-side, so we can't read window.deckDesktop —
    // the Electron shell tags its User-Agent with "DeckDesktop", which we sniff here.
    const siteUrl = getSiteUrl(request);
    const emailRedirectTo = isDesktopAppRequest(request) ? DESKTOP_AUTH_CALLBACK : `${siteUrl}/auth/callback`;
    // Before Deck opens, a link goes only to an email that already has an account.
    const { error: otpError } = await serverScopedSupabase.auth.signInWithOtp({
      email,
      options: {
        emailRedirectTo,
        captchaToken,
        shouldCreateUser: openToNewAccounts(),
      },
    });

    if (otpError) {
      console.error('Error sending magic link:', otpError);
      if ((otpError as { code?: string }).code === 'otp_disabled' || /signups not allowed/i.test(otpError.message)) {
        return json<ActionData>({ error: NOT_OPEN_YET }, { headers: response.headers });
      }
      if (
        otpError.message.includes('Email rate limit exceeded') ||
        (otpError as { code?: string }).code === 'over_email_send_rate_limit' ||
        otpError.message.toLowerCase().includes('rate limit')
      ) {
        return json<ActionData>(
          {
            error: 'Please wait before requesting another magic link',
            cooldown: true,
          },
          { headers: response.headers },
        );
      }
      // Supabase refuses the send when the Turnstile token is missing, expired or already spent.
      if (otpError.message.toLowerCase().includes('captcha')) {
        return json<ActionData>(
          { error: "We couldn't confirm this request came from you. Please try again." },
          { headers: response.headers },
        );
      }
      return json<ActionData>({ error: otpError.message }, { headers: response.headers });
    }

    return json<ActionData>(
      {
        success: true,
        message: 'Check your email for the magic link.',
      },
      { headers: response.headers },
    );
  } catch (error) {
    console.error('Sign-in exception:', error);
    return json<ActionData>(
      {
        error: 'An unexpected error occurred. Please try again.',
      },
      { headers: response.headers },
    );
  }
};

const SignInForm = () => {
  const [email, setEmail] = useState('');
  const [isSubmitted, setIsSubmitted] = useState(false);
  const actionData = useActionData<typeof action>();
  const siteKey = useLoaderData<typeof loader>()?.turnstileSiteKey ?? null;
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);
  const [captchaTrouble, setCaptchaTrouble] = useState(false);
  const browserUrl = actionData && 'openInBrowser' in actionData ? actionData.openInBrowser : null;
  const [inBrowser, setInBrowser] = useState(false);
  const navigation = useNavigation();
  const navigate = useNavigate();
  const submit = useSubmit();
  const { user, isLoading } = useAuth();

  const [cooldownSeconds, setCooldownSeconds] = useState(7);
  const [cooldownActive, setCooldownActive] = useState(false);

  useEffect(() => {
    if (isError(actionData) && actionData.cooldown) {
      setCooldownActive(true);
      setCooldownSeconds(7);

      const interval = setInterval(() => {
        setCooldownSeconds((prev) => {
          if (prev <= 1) {
            clearInterval(interval);
            setCooldownActive(false);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      return () => clearInterval(interval);
    }
  }, [actionData]);

  // Deck for Mac: Google goes to the default browser. Navigating to its address is enough, because the
  // app sends any address that is not Deck's to the default browser and keeps this page where it is.
  useEffect(() => {
    if (!browserUrl) return;
    setInBrowser(true);
    window.location.assign(browserUrl);
  }, [browserUrl]);

  useEffect(() => {
    if (isSuccess(actionData)) {
      setIsSubmitted(true);
      // Top of the funnel. Anonymous at this point, so it joins the person on identify.
      track('signin_link_sent', { method: 'magic_link' });
    }
  }, [actionData]);

  // A rate limit is a real drop-off cause, not noise: it is people trying twice and giving up.
  useEffect(() => {
    if (isError(actionData)) {
      track('signin_failed', { reason: actionData.cooldown ? 'cooldown' : 'error' });
    }
  }, [actionData]);

  useEffect(() => {
    if (user && !isLoading) {
      navigate('/dashboard', { replace: true });
    }
  }, [user, isLoading, navigate]);

  // Only reachable when a session appears in this tab after the page was already open (signed in
  // elsewhere). Just the ground and dots, not the magic-link screen — nothing is being verified.
  if (user) {
    return <SignInWaiting />;
  }

  if (isLoading) {
    return <SignInWaiting />;
  }

  const isSubmitting = navigation.state === 'submitting';
  const isDisabled = isSubmitting || cooldownActive;
  // Without this line the person sees Cloudflare's "Verification failed" over a grey button and no way on.
  const captchaError = captchaTrouble
    ? "We couldn't verify this browser. Continue with Google, or open Deck in Chrome or Safari."
    : null;

  return (
    <>
      <SignInUI
        FormComponent={Form}
        view={inBrowser ? 'continue-in-browser' : isSubmitted ? 'magic-link-sent' : 'form'}
        onOpenBrowserAgain={browserUrl ? () => window.location.assign(browserUrl) : undefined}
        email={email}
        onEmailChange={setEmail}
        onReset={() => {
          setEmail('');
          setIsSubmitted(false);
          setInBrowser(false);
          // The last token went with the link just sent; the form's fresh check brings a new one.
          setCaptchaToken(null);
        }}
        showGoogleSignIn
        onGoogleSignIn={() => {
          track('signin_started', { method: 'google' });
          submit({ intent: 'google' }, { method: 'post' });
        }}
        error={isError(actionData) ? actionData.error : null}
        notice={captchaError}
        showCooldownMessage={isError(actionData) && Boolean(actionData.cooldown)}
        cooldownSeconds={cooldownSeconds}
        isSubmitting={isSubmitting}
        isDisabled={isDisabled}
        isSubmitReady={!siteKey || Boolean(captchaToken)}
        captcha={
          siteKey ? (
            <>
              <input type="hidden" name="captchaToken" value={captchaToken ?? ''} />
              <TurnstileWidget
                siteKey={siteKey}
                resetSignal={actionData}
                onToken={(token) => {
                  setCaptchaToken(token);
                  if (token) setCaptchaTrouble(false);
                }}
                onTrouble={() => setCaptchaTrouble(true)}
              />
            </>
          ) : null
        }
      />
      <ToastContainer />
    </>
  );
};

export default function SignIn() {
  return (
    <ToastProvider>
      <SignInForm />
    </ToastProvider>
  );
};
